import React, { useState } from 'react';
import { Form, Input, InputNumber, DatePicker, Upload, Button, Row, Col } from 'antd';
import moment from 'moment';
import SystemModal from 'components/Shared/Modal';
import { ReceiptIcon, UploadIcon } from 'components/Shared/Icons';
import useTranslation from 'utils/hooks/useTranslation';
import useIsFormValid from 'utils/hooks/useIsFormValid';
import notify from 'utils/notificationUtils';
import { MAX_UPLOAD_SIZE_BYTES } from 'constants.js';

import styles from './style.module.scss';

const PROOF_OF_TRANSFER_ACCEPT = '.pdf,.png,.jpg,.jpeg';

const fieldValidationConfig = [
  { name: 'amountSent', required: true },
  { name: 'transferDate', required: true },
  { name: 'referenceId', required: true },
];

const formatFileSize = bytes => `${Math.max(1, Math.round(bytes / 1024))} KB`;

const AMOUNT_ALLOWED_CONTROL_KEYS = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
  'Home',
  'End',
];

// InputNumber renders a text input under the hood, so nothing stops a user from typing
// letters (or 'e'/'-' for scientific/negative notation) — only numbers and a single
// decimal point should be allowed.
const handleAmountKeyDown = event => {
  if (event.ctrlKey || event.metaKey || AMOUNT_ALLOWED_CONTROL_KEYS.includes(event.key)) {
    return;
  }

  const isDigit = /^[0-9]$/.test(event.key);
  const isFirstDecimalPoint = event.key === '.' && !event.currentTarget.value.includes('.');

  if (!isDigit && !isFirstDecimalPoint) {
    event.preventDefault();
  }
};

const handleAmountPaste = event => {
  const pastedText = event.clipboardData.getData('text');

  if (!/^[0-9]*\.?[0-9]*$/.test(pastedText)) {
    event.preventDefault();
  }
};

const ConfirmTransferModal = ({ visible, onClose, currencySymbol }) => {
  const { translateText } = useTranslation();
  const [form] = Form.useForm();
  const { isFormValid, validateForm } = useIsFormValid(form, fieldValidationConfig);
  const [proofFile, setProofFile] = useState(null);

  const resetModal = () => {
    form.resetFields();
    setProofFile(null);
  };

  const handleCancel = () => {
    resetModal();
    onClose();
  };

  const handleFinish = () => {
    notify({
      type: 'success',
      message: translateText(
        'corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.success',
      ),
    });
    resetModal();
    onClose();
  };

  const handleRemoveProof = () => {
    setProofFile(null);
  };

  const handleBeforeUpload = file => {
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.import.maxFileSize.error'),
      });
      return false;
    }

    setProofFile(file);
    return false;
  };

  const label = key => translateText(`corporateAccounts.wallet.dashboard.addFunds.${key}`) + ' *';

  return (
    <SystemModal
      title={
        <span className={styles.dashboardModalTitle}>
          {translateText('corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.title')}
        </span>
      }
      titleLevel={5}
      width={440}
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      bodyStyle={{ padding: '20px' }}
      className={`${styles.dashboardModal} ${styles.confirmTransferModal}`}
      destroyOnClose
      centered
      data-testid="confirmTransferModal"
    >
      <div className={styles.confirmTransferCopy}>
        {translateText('corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.copy')}
      </div>
      <Form
        form={form}
        layout="vertical"
        colon={false}
        onFieldsChange={validateForm}
        onFinish={handleFinish}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="amountSent"
              label={label('confirmTransferModal.amountSent')}
              required={false}
              rules={[
                {
                  required: true,
                  message: translateText(
                    'corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.amountSentRequired',
                  ),
                },
              ]}
            >
              <InputNumber
                min={0.01}
                controls={false}
                placeholder={`${currencySymbol}0.00`}
                className={styles.confirmTransferInput}
                data-testid="confirmTransferModalAmount"
                onKeyDown={handleAmountKeyDown}
                onPaste={handleAmountPaste}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="transferDate"
              label={label('confirmTransferModal.transferDate')}
              required={false}
              rules={[
                {
                  required: true,
                  message: translateText(
                    'corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.transferDateRequired',
                  ),
                },
              ]}
            >
              <DatePicker
                className={styles.confirmTransferInput}
                disabledDate={date => date && date.isAfter(moment(), 'day')}
                data-testid="confirmTransferModalDate"
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="referenceId"
          label={label('confirmTransferModal.referenceId')}
          required={false}
          rules={[
            {
              required: true,
              message: translateText(
                'corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.referenceIdRequired',
              ),
            },
          ]}
        >
          <Input
            placeholder={translateText(
              'corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.referenceIdPlaceholder',
            )}
            className={styles.confirmTransferInput}
            data-testid="confirmTransferModalReference"
          />
        </Form.Item>
      </Form>

      <div className={styles.confirmTransferFieldLabel}>
        {label('confirmTransferModal.proofOfTransfer')}
      </div>
      {proofFile ? (
        <div
          className={styles.confirmTransferProofAttached}
          data-testid="confirmTransferModalProofAttached"
        >
          <span className={styles.confirmTransferProofIcon}>
            <ReceiptIcon />
          </span>
          <div className={styles.confirmTransferProofDetails}>
            <div className={styles.confirmTransferProofName}>{proofFile.name}</div>
            <div className={styles.confirmTransferProofSize}>{formatFileSize(proofFile.size)}</div>
          </div>
          <span
            className={styles.confirmTransferProofRemove}
            onClick={handleRemoveProof}
            data-testid="confirmTransferModalProofRemove"
          >
            {translateText('corporateAccounts.common.remove')}
          </span>
        </div>
      ) : (
        <Upload
          accept={PROOF_OF_TRANSFER_ACCEPT}
          beforeUpload={handleBeforeUpload}
          showUploadList={false}
          className={styles.confirmTransferProofUploadWrapper}
          data-testid="confirmTransferModalProofUpload"
        >
          <div className={styles.confirmTransferProofDropzone}>
            <span className={styles.confirmTransferProofDropzoneIcon}>
              <UploadIcon />
            </span>
            <div className={styles.confirmTransferProofDropzoneTitle}>
              {translateText(
                'corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.proofOfTransferAttach',
              )}
            </div>
            <div className={styles.confirmTransferProofDropzoneSubtitle}>
              {translateText(
                'corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.proofOfTransferFileTypes',
              )}
            </div>
          </div>
        </Upload>
      )}

      <Button
        type="primary"
        block
        className={styles.confirmTransferSubmit}
        disabled={!isFormValid || !proofFile}
        onClick={() => form.submit()}
        data-testid="confirmTransferModalSubmit"
      >
        {translateText('corporateAccounts.wallet.dashboard.addFunds.confirmTransferModal.submit')}
      </Button>
    </SystemModal>
  );
};

export default ConfirmTransferModal;
